<template>
  <v-container
    fill-height
    fluid
  >
    <v-layout
      v-if="!$apollo.loading && getWhiteLabel"
      wrap
    >
      <v-flex
        md5
        xs12
        mb-0
        pt-0
        pb-0
      >
        <h2>{{ $t('users.users') }}</h2>
      </v-flex>
      <v-flex
        md7
        xs6
        mb-0
        mt-md-5
        pb-0
        text-right
      >
        <v-text-field
          v-model.trim="search"
          name="search"
          max-length="100"
          style="width:220px"
          prepend-inner-icon="mdi-text-box-search-outline"
          @change="reload()"
        >
          <template v-slot:append>
            <v-icon
              v-if="search"
              style="cursor:pointer"
              @click="removeSearch()"
            >
              mdi-close
            </v-icon>
          </template>
        </v-text-field>
      </v-flex>
      <v-flex
        v-if="!$apollo.loading"
      >
        <v-card>
          <v-flex v-if="whiteLabelUsers">
            <v-data-table
              v-if="whiteLabelUsers && whiteLabelUsers.data.length > 0" 
              :class="$vuetify.breakpoint.xs ? '' : 'text-no-wrap'" 
              :headers="computedHeaders"
              :footer-props="{
                'items-per-page-options': [10, 15, 25, 50, 100, 200]
              }"
              :single-expand="true"
              show-expand
              :locale="$i18n.locale"
              :items="whiteLabelUsers.data"
              :items-per-page="whiteLabelUsers.per_page"
              :page="whiteLabelUsers.current_page"
              :server-items-length="whiteLabelUsers.total"
              :hide-default-footer="_.isEmpty(whiteLabelUsers.data)"
              @update:items-per-page="reload($event, 'count')"
              @update:page="reload($event, 'page')"
            >
              <template v-slot:expanded-item="{ item }">
                <td
                  :colspan="computedHeaders.length + 1"
                  class="table_details"
                >
                  <user-details :user-uuid="item.uuid" />
                </td>
              </template>
              <template v-slot:item.avatar="{ item }">
                <v-avatar
                  v-if="item.avatar"
                  class="mt-3 mb-3"
                  :tile="false"
                  :size="40"
                >
                  <v-img
                    :src="item.avatar"
                    :lazy-src="item.avatar"
                    max-height="40"
                    contain
                  />
                </v-avatar>
                <v-avatar
                  v-else
                  class="mt-3 mb-3"
                  :tile="false"
                  :size="40"
                  :color="$vuetify.theme.dark ? $vuetify.theme.themes.dark.background : $vuetify.theme.themes.light.background"
                >
                  <strong>
                    <a>{{ item.name | uppercase | substring(0,2) }}</a>
                  </strong>
                </v-avatar>
              </template>
              <template v-slot:item.name="{ item }">
                <b>{{ item.name }}</b>
                <v-tooltip
                  v-if="item.vip_client"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-icon
                      style="margin: -2px 5px 0px; font-size:16px;"
                      v-on="on"
                    >
                      {{ item.vip_client_valid_till ? 'mdi-clock-outline' : 'mdi-all-inclusive' }}
                    </v-icon>
                  </template>
                  <span v-if="item.vip_client_valid_till">
                    {{ $t('users.trialValidTill', {'date': $moment(item.vip_client_valid_till).format('LL')}) }}
                  </span>
                  <span v-else>
                    {{ $t('users.trialValidAlways') }}
                  </span>
                </v-tooltip>
                <br>
                <small>{{ item.email }}</small>
                <div
                  v-if="item.telegram"
                  class="mb-2"
                >
                  <small><a
                    :href="'https://t.me/' + item.telegram.replace('@', '')"
                    target="_blank"
                  >@{{ item.telegram.replace('@', '') }}</a></small>
                </div>
              </template>
              <template v-slot:item.trading_balance="{ item }">
                <span
                  v-for="(b, i) in item.trading_balance"
                  :key="i"
                >
                  {{ b.base_currency | renderSymbol }} {{ b.balance }}<br>
                </span>
              </template>
              <template v-slot:item.id="{ item }">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <img
                      v-if="item.country_code"
                      :src="require('@/images/flags/' + item.country_code + '.svg')"
                      :alt="item.country_name"
                      width="24"
                      v-on="on"
                    >
                  </template>
                  {{ item.country_name }}
                </v-tooltip>
              </template>
              <template v-slot:item.purchase="{ item }">
                <div class="text-wrap">
                  <v-chip
                    v-if="item.purchases.length === 0"
                    class="mt-3"
                    :class="'error'"
                    small
                    outlined
                  >
                    {{ $t('globals.no') }}
                  </v-chip>
                  <purchase
                    v-else-if="item.purchases.length === 1"
                    :user="item.purchases[0]"
                  />
                  <small v-else>{{ item.purchases.length }}</small>
                </div>
              </template>
              <template v-slot:item.created_at="{ item }">
                <small>{{ item.created_at | moment('L') }}
                </small>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-menu
                  offset-y
                  left
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      outlined 
                      small
                      text
                      v-on="on"
                    >
                      {{ $t('globals.manageButton') }}
                    </v-btn>
                  </template>
                  <v-sheet>
                    <v-list-item
                      dense
                      @click="editUser(item)"
                    >
                      <v-list-item-title>{{ $t('globals.editButton') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      dense
                      @click="editBalance(item)"
                    >
                      <v-list-item-title>{{ $t('users.editBalance') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      dense
                      @click="trialPeriod(item)"
                    >
                      <v-list-item-title>{{ $t('users.trial') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      dense
                      @click="changeReferrer(item)"
                    >
                      <v-list-item-title>{{ $t('users.changeReferrer') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      dense
                      @click="deleteUser(item)"
                    >
                      <v-list-item-title>{{ $t('globals.deleteButton') }}</v-list-item-title>
                    </v-list-item>
                  </v-sheet>
                </v-menu>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex
            v-if="whiteLabelUsers && whiteLabelUsers.data.length == 0"
            mt-4 
            class="not-found"
          >
            <v-icon>mdi-ghost</v-icon>
            <p>{{ $t('users.usersNotFound') }}</p>
          </v-flex>
        </v-card>
      </v-flex>
      <trial-period-dialog
        v-if="trialPeriodDialog"
        :show-dialog="trialPeriodDialog"
        :user="selectedUser"
        @closeTrialPeriodDialog="closeTrialPeriodDialog"
      />
      <user-edit-dialog
        v-if="userEditDialog"
        :show-dialog="userEditDialog"
        :user="selectedUser"
        @closeUserEditDialog="closeUserEditDialog"
      />
      <change-referrer-dialog
        v-if="changeReferrerDialog"
        :show-dialog="changeReferrerDialog"
        :whitelabel-uuid="getWhiteLabel.uuid"
        :user="selectedUser"
        @closeChangeReferrerDialog="closeChangeReferrerDialog"
      />
      <EditBalanceDialog
        v-if="balanceEditDialog"
        :show-dialog="balanceEditDialog"
        :user="selectedUser"
        @closeEditBalanceDialog="closeEditBalanceDialog"
      />
      <user-delete-dialog
        v-if="userDeleteDialog"
        :show-dialog="userDeleteDialog"
        :user="selectedUser"
        @closeUserDeleteDialog="closeUserDeleteDialog"
      />
    </v-layout>
    <v-layout
      v-if="!$apollo.loading && !getWhiteLabel"
      wrap
      justify-center
    >
      <helper-start-white-label />
    </v-layout>
  </v-container>
</template>

<script>

import WHITELABEL_USERS_QUERY from '@/graphql/queries/WhiteLabelUsers.gql';
import TrialPeriodDialog from "@/views/Users/partial/TrialPeriodDialog.vue";
import UserEditDialog from "@/views/Users/partial/EditDialog.vue";
import UserDeleteDialog from "@/views/Users/partial/DeleteDialog.vue";
import ChangeReferrerDialog from "@/views/Users/partial/ChangeReferrerDialog.vue";
import UserDetails from "@/views/Users/partial/Details.vue";
import Purchase from "@/views/Users/partial/Purchase";
import { mapGetters } from 'vuex'
import EditBalanceDialog from "@/views/Users/partial/BalanceEditDialog.vue";

export default {
  name: 'WhiteLabelUsers',
  components: {
    EditBalanceDialog,
    TrialPeriodDialog,
    UserEditDialog,
    UserDeleteDialog,
    ChangeReferrerDialog,
    UserDetails,
    Purchase
  },
  data() {
    return {
      selectedUser: null,
      userEditDialog: false,
      balanceEditDialog: false,
      userDeleteDialog: false,
      trialPeriodDialog: false,
      changeReferrerDialog: false,
      loading: false,
      search: null,
      params: {
        whitelabel_uuid: null,
        page: 1,
        count: 25
      },
      headers: [
        {
          sortable: false,
          text: '',
          value: 'id',
          width: 30
        },
        {
          sortable: false,
          text: '',
          value: 'avatar',
          width: 50
        },
        {
          sortable: false,
          text: this.$t('globals.name'),
          value: 'name'
        },
        {
          sortable: false,
          text: this.$t('users.purchase'),
          value: 'purchase',
          align: 'center',
          width: '20%'

        },
        {
          sortable: false,
          text: this.$t('users.strategies'),
          value: 'count_connections'
        },
        {
          sortable: false,
          text: this.$t('users.deposits'),
          value: 'trading_balance'
        },
        {
          sortable: false,
          text: this.$t('whitelabel.createdAt'),
          value: 'created_at'
        },
        {
          sortable: false,
          text: "",
          value: 'actions'
        },
      ]
    }
    
  },
  computed: {
    ...mapGetters('user', ['getUser']),
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
    computedHeaders() {
      return this.headers.filter(item => {
        if (this.getWhiteLabel.modules.length > 0) {
          const module = this.getWhiteLabel.modules.find(item => item.module == 'spot' || item.module == 'futures')
          if (!module && (item.value == 'count_connections' | item.value == 'trading_balance')) {
            return false
          }
        }
        return true
      })  
    },
  },
  watch: {
    getWhiteLabel(val){
      console.log("getWhiteLabel")
      this.search = null
      this.reload(null, null, this.getWhiteLabel.uuid)
    },
    '$route.query.search': {
      handler(val) {
        if(val){
          this.search = val;
          this.params.search = val;
        } else {
          this.search = null;
        }
        this.reload()
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    if (this.getWhiteLabel) {
      this.params.whitelabel_uuid = this.getWhiteLabel.uuid
    }
  },
  methods: {
    trialPeriod(item) {
      this.selectedUser = item
      this.trialPeriodDialog = true
    },
    editUser(item) {
      this.selectedUser = item
      this.userEditDialog = true
    },
    editBalance(item) {
      this.selectedUser = item
      this.balanceEditDialog = true
    },
    changeReferrer(item) {
      this.selectedUser = item
      this.changeReferrerDialog = true
    },
    deleteUser(item) {
      this.selectedUser = item
      this.userDeleteDialog = true
    },
    closeTrialPeriodDialog(refresh) {
      this.trialPeriodDialog = false
      if (refresh) {
        this.$apollo.queries.whiteLabelUsers.refetch()
      }
    },
    closeUserEditDialog(refresh) {
      this.userEditDialog = false
      if (refresh) {
        this.$apollo.queries.whiteLabelUsers.refresh()
      }
    },
    closeChangeReferrerDialog(refresh) {
      this.changeReferrerDialog = false
      if (refresh) {
        this.$apollo.queries.whiteLabelUsers.refresh()
      }
    },
    closeUserDeleteDialog(refresh) {
      this.userDeleteDialog = false
      if (refresh) {
        this.$apollo.queries.whiteLabelUsers.refresh()
      }
    },
    closeEditBalanceDialog(refresh) {
      this.balanceEditDialog = false
      if (refresh) {
        this.$apollo.queries.whiteLabelUsers.refresh()
      }
    },
    removeSearch() {
      let query = { ...this.$route.query}
      delete query.search
      this.$router.replace({ query })
      this.search = null
      this.reload()
    },
    reload(value = null, from = null, uuid = null) {

      if (!this.$apollo.queries.whiteLabelUsers) {
        return
      }

      if (value && from) {
        this.params[from] = value
      }

      if (uuid) {
        this.params.whitelabel_uuid = uuid
      }
      if (this.search) {
        if (this.search.length < 2) {
          return
        }
        this.params.search = this.search
      } else {
        delete(this.params.search)
      }
      this.$apollo.queries.whiteLabelUsers.refresh();
    },
  },
  apollo: {
    whiteLabelUsers: {
      query: WHITELABEL_USERS_QUERY,
      fetchPolicy: 'network-only',
      variables() {
        return this.params
      },
      skip() {
        return !this.params.whitelabel_uuid
      }
    },
  }
}
</script>

<style lang="scss">


</style>
